import * as React from 'react'
import { isEqual } from 'date-fns'
import { LoadingContext } from 'contexts/loadingContext'
import { ErrorContext } from 'contexts/errorContext'
import { DeliveryDateContext } from 'contexts/deliveryDateContext'
import useFetchWithAuth from 'hooks/useFetchWithAuth'

/**
 * Reducer function
 */
const ordersReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ORDERS_DATA': {
      return action.payload
    }

    case 'UPDATE_ORDER_STATUS': {
      const { orderId, completed } = action.payload
      return {
        ...state,
        orders: state.orders.map((order) =>
          order._id === orderId ? { 
            ...order,
            completed,
            items: order.items.map((item) => ({ ...item, completed }))
          } : order
        ),
      }
    }

    case 'UPDATE_ITEM_STATUS': {
      const { orderId, itemId, completed } = action.payload
      return {
        ...state,
        orders: state.orders.map((order) => {
          if (order._id === orderId) {
            const items = order.items.map((item) => (item._id === itemId ? { ...item, completed } : item))
            return {
              ...order,
              completed: items.every((item) => item.completed) === false && order.completed ? false : order.completed,
              items,
            }
          }

          return order
        }),
      }
    }

    case 'DELETE_ORDER': {
      const { orderId } = action.payload
      return {
        ...state,
        orders: state.orders.filter((order) => order._id !== orderId),
      }
    }

    default:
      return state
  }
}

/**
 * Creating context
 */
export const OrdersContext = React.createContext()

/**
 * Context provider component
 */
export const OrdersProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(ordersReducer, { orders: [], deliveryDate: null })
  const { setLoading } = React.useContext(LoadingContext)
  const { showError } = React.useContext(ErrorContext)
  const { updateDeliveryDate } = React.useContext(DeliveryDateContext)
  const fetchWithAuth = useFetchWithAuth()

  const fetchOrders = React.useCallback(
    async (deliveryDate, fromCache = true) => {
      if (! (deliveryDate && (!isEqual(deliveryDate, state.deliveryDate) || !fromCache))) return

      setLoading(true)

      try {
        const params = new URLSearchParams({ deliveryDate })
        const response = await fetchWithAuth(`/api/orders?${params}`)
        const orders = await response.json()
        dispatch({ type: 'SET_ORDERS_DATA', payload: { orders, deliveryDate } })
        updateDeliveryDate(deliveryDate)
      } catch (error) {
        showError('Failed to fetch orders. Please try again or refresh the page.')
        console.error(error.message)
      } finally {
        setLoading(false)
      }
    },
    [state.deliveryDate]
  )

  const refreshOrders = async () => {
    await fetchOrders(state.deliveryDate, false)
  }

  const resetOrders = () => {
    dispatch({ type: 'SET_ORDERS_DATA', payload: { orders: [], deliveryDate: null } })
  }

  const updateOrderStatus = async (orderId, completed) => {
    dispatch({ type: 'UPDATE_ORDER_STATUS', payload: { orderId, completed } })

    try {
      await fetchWithAuth(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed }),
      })
    } catch (error) {
      showError('Failed to update the order status. Please try again or refresh the page.')
      console.error(error.message)
    }
  }

  const updateItemStatus = async (orderId, itemId, completed) => {
    dispatch({ type: 'UPDATE_ITEM_STATUS', payload: { orderId, itemId, completed } })

    try {
      await fetchWithAuth(`/api/orders/${orderId}/items/${itemId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed }),
      })
    } catch (error) {
      showError('Failed to update the item status. Please try again or refresh the page.')
      console.error(error.message)
    }
  }

  const deleteOrder = async (orderId) => {
    dispatch({ type: 'DELETE_ORDER', payload: { orderId } })

    try {
      await fetchWithAuth(`/api/orders/${orderId}`, {
        method: 'DELETE'
      })
    } catch (error) {
      showError('Failed to delete the order. Please try again or refresh the page.')
      console.error(error.message)
    }
  }

  return (
    <OrdersContext.Provider value={{ ...state, fetchOrders, refreshOrders, resetOrders, updateOrderStatus, updateItemStatus, deleteOrder }}>
      {children}
    </OrdersContext.Provider>
  )
}
